// src/app/shared/notifications/notifications.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, expand, finalize, map, reduce, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
    ApiPagedResponse,
    ApiResponse,
    RequiredNotificationActionVm,
    UserNotificationsDto,
    YywwDto,
} from './notifications.model';

@Injectable({ providedIn: 'root' })
export class RequiredNotificationsService {
    private readonly baseUrl = environment.apiBaseUrl;

    private readonly emptyNotifications: UserNotificationsDto = {
        yywWs: [],
        dayOffRequestGroupsNeedToApproveBySupervisor: [],
        bookedHoursNeedToApproveBySupervisor: [],
        bookedHoursNeedToApproveBySelf: [],
    };

    private readonly notificationsSubject = new BehaviorSubject<UserNotificationsDto>(
        this.emptyNotifications,
    );
    private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
    private readonly isActionRunningSubject = new BehaviorSubject<boolean>(false);
    private readonly errorMessageSubject = new BehaviorSubject<string | null>(null);
    private readonly currentFullYearWeekSubject = new BehaviorSubject<string | null>(null);

    readonly notifications$ = this.notificationsSubject.asObservable();
    readonly isLoading$ = this.isLoadingSubject.asObservable();
    readonly isActionRunning$ = this.isActionRunningSubject.asObservable();
    readonly errorMessage$ = this.errorMessageSubject.asObservable();
    readonly currentFullYearWeek$ = this.currentFullYearWeekSubject.asObservable();

    readonly hasBlockingNotifications$ = this.notifications$.pipe(
        map((notifications) => this.getTotalNotificationCount(notifications) > 0),
    );

    constructor(private readonly http: HttpClient) { }

    initializeForCurrentWeek(date: Date = new Date()): Observable<UserNotificationsDto> {
        this.isLoadingSubject.next(true);
        this.errorMessageSubject.next(null);

        return this.resolveFullYearWeekForDate(date).pipe(
            tap((fullYearWeek) => this.currentFullYearWeekSubject.next(fullYearWeek)),
            switchMap((fullYearWeek) => this.getUserNotifications(fullYearWeek)),
            tap((notifications) => this.notificationsSubject.next(this.normalizeNotifications(notifications))),
            catchError((error) => {
                console.error('Failed to initialize required notifications', error);
                this.errorMessageSubject.next('Failed to load required notifications.');
                this.notificationsSubject.next(this.emptyNotifications);
                return of(this.emptyNotifications);
            }),
            finalize(() => this.isLoadingSubject.next(false)),
        );
    }

    loadRequiredNotifications(): void {
        this.initializeForCurrentWeek().subscribe();
    }

    refreshCurrentWeek(): Observable<UserNotificationsDto> {
        const fullYearWeek = this.currentFullYearWeekSubject.value;

        if (!fullYearWeek) {
            return this.initializeForCurrentWeek();
        }

        this.isLoadingSubject.next(true);
        this.errorMessageSubject.next(null);

        return this.getUserNotifications(fullYearWeek).pipe(
            tap((notifications) => this.notificationsSubject.next(this.normalizeNotifications(notifications))),
            catchError((error) => {
                console.error('Failed to refresh required notifications', error);
                this.errorMessageSubject.next('Failed to refresh required notifications.');
                return of(this.notificationsSubject.value);
            }),
            finalize(() => this.isLoadingSubject.next(false)),
        );
    }

    resolveAction(action: RequiredNotificationActionVm): Observable<UserNotificationsDto> {
        this.isActionRunningSubject.next(true);
        this.errorMessageSubject.next(null);

        return this.runAction(action).pipe(
            tap(() => this.removeResolvedNotification(action)),
            switchMap(() => this.refreshCurrentWeek()),
            catchError((error) => {
                console.error('Failed to resolve required notification', error);
                this.errorMessageSubject.next('Failed to update notification.');
                return of(this.notificationsSubject.value);
            }),
            finalize(() => this.isActionRunningSubject.next(false)),
        );
    }

    approveDayOffRequest(groupId: number): void {
        this.resolveAction({ kind: 'dayOffSupervisorApproval', id: groupId, isApproved: true }).subscribe();
    }

    rejectDayOffRequest(groupId: number): void {
        this.resolveAction({ kind: 'dayOffSupervisorApproval', id: groupId, isApproved: false }).subscribe();
    }

    approveBookedHour(bookedHourId: number): void {
        this.resolveAction({ kind: 'bookedHourSupervisorApproval', id: bookedHourId, isApproved: true }).subscribe();
    }

    rejectBookedHour(bookedHourId: number): void {
        this.resolveAction({ kind: 'bookedHourSupervisorApproval', id: bookedHourId, isApproved: false }).subscribe();
    }

    requestBookedHourChange(bookedHourId: number, newBookedHours: number): void {
        this.resolveAction({
            kind: 'bookedHourSupervisorApproval',
            id: bookedHourId,
            newBookedHours,
        }).subscribe();
    }

    approveBookedHourWithChange(bookedHourId: number, newBookedHours: number): void {
        this.resolveAction({
            kind: 'bookedHourSupervisorApproval',
            id: bookedHourId,
            newBookedHours,
            isApproved: true,
        }).subscribe();
    }

    approveSelfBookedHour(bookedHourId: number): void {
        this.resolveAction({ kind: 'bookedHourSelfApproval', id: bookedHourId, isApproved: true }).subscribe();
    }

    rejectSelfBookedHour(bookedHourId: number): void {
        this.resolveAction({ kind: 'bookedHourSelfApproval', id: bookedHourId, isApproved: false }).subscribe();
    }

    approveAllPending(): Observable<UserNotificationsDto> {
        const notifications = this.notificationsSubject.value;
        const actions = this.buildPendingActions(notifications, true);

        if (!actions.length) {
            return this.refreshCurrentWeek();
        }

        this.isActionRunningSubject.next(true);
        this.errorMessageSubject.next(null);

        return forkJoin(actions.map((action) => this.runAction(action))).pipe(
            tap(() => actions.forEach((action) => this.removeResolvedNotification(action))),
            switchMap(() => this.refreshCurrentWeek()),
            catchError((error) => {
                console.error('Failed to approve all notifications', error);
                this.errorMessageSubject.next('Failed to approve all notifications.');
                return of(this.notificationsSubject.value);
            }),
            finalize(() => this.isActionRunningSubject.next(false)),
        );
    }

    getTotalNotificationCount(notifications: UserNotificationsDto | null | undefined): number {
        if (!notifications) {
            return 0;
        }

        return (
            (notifications.dayOffRequestGroupsNeedToApproveBySupervisor?.length ?? 0) +
            (notifications.bookedHoursNeedToApproveBySupervisor?.length ?? 0) +
            (notifications.bookedHoursNeedToApproveBySelf?.length ?? 0)
        );
    }

    getPendingActionCount(notifications: UserNotificationsDto | null | undefined): number {
        if (!notifications) {
            return 0;
        }

        const dayOffCount =
            notifications.dayOffRequestGroupsNeedToApproveBySupervisor?.filter(
                (item) => item.isApproved === null,
            ).length ?? 0;

        return (
            dayOffCount +
            (notifications.bookedHoursNeedToApproveBySupervisor?.length ?? 0) +
            (notifications.bookedHoursNeedToApproveBySelf?.length ?? 0)
        );
    }

    private runAction(action: RequiredNotificationActionVm): Observable<void> {
        switch (action.kind) {
            case 'dayOffSupervisorApproval':
                return action.isApproved === true
                    ? this.approveDayOffRequestGroup(action.id)
                    : this.rejectDayOffRequestGroup(action.id);

            case 'bookedHourSupervisorApproval':
            case 'bookedHourSelfApproval':
                return this.patchBookedHourApproval(action.id, this.buildBookedHourApprovalBody(action));

            default:
                return throwError(() => new Error('Unsupported notification action.'));
        }
    }

    private buildBookedHourApprovalBody(
        action: RequiredNotificationActionVm,
    ): { isApproved?: boolean; newBookedHours?: number | null } {
        const body: { isApproved?: boolean; newBookedHours?: number | null } = {};

        body.newBookedHours =
            typeof action.newBookedHours === 'number'
                ? action.newBookedHours
                : null;

        if (typeof action.isApproved === 'boolean') {
            body.isApproved = action.isApproved;
        }

        return body;
    }

    private buildPendingActions(
        notifications: UserNotificationsDto,
        isApproved: boolean,
    ): RequiredNotificationActionVm[] {
        return [
            ...(notifications.dayOffRequestGroupsNeedToApproveBySupervisor ?? [])
                .filter((item) => item.isApproved === null)
                .map((item) => ({
                    kind: 'dayOffSupervisorApproval' as const,
                    id: item.id,
                    isApproved,
                })),
            ...(notifications.bookedHoursNeedToApproveBySupervisor ?? []).map((item) => ({
                kind: 'bookedHourSupervisorApproval' as const,
                id: item.id,
                isApproved,
            })),
            ...(notifications.bookedHoursNeedToApproveBySelf ?? []).map((item) => ({
                kind: 'bookedHourSelfApproval' as const,
                id: item.id,
                isApproved,
            })),
        ];
    }

    private removeResolvedNotification(action: RequiredNotificationActionVm): void {
        const current = this.notificationsSubject.value;

        this.notificationsSubject.next({
            yywWs: current.yywWs ?? [],

            dayOffRequestGroupsNeedToApproveBySupervisor:
                action.kind === 'dayOffSupervisorApproval'
                    ? current.dayOffRequestGroupsNeedToApproveBySupervisor.filter((item) => item.id !== action.id)
                    : current.dayOffRequestGroupsNeedToApproveBySupervisor,

            bookedHoursNeedToApproveBySupervisor:
                action.kind === 'bookedHourSupervisorApproval'
                    ? current.bookedHoursNeedToApproveBySupervisor.filter((item) => item.id !== action.id)
                    : current.bookedHoursNeedToApproveBySupervisor,

            bookedHoursNeedToApproveBySelf:
                action.kind === 'bookedHourSelfApproval'
                    ? current.bookedHoursNeedToApproveBySelf.filter((item) => item.id !== action.id)
                    : current.bookedHoursNeedToApproveBySelf,
        });
    }

    private getUserNotifications(fullYearWeek: string): Observable<UserNotificationsDto> {
        const params = new HttpParams().set('fullYearWeek', fullYearWeek);

        return this.http
            .get<ApiResponse<UserNotificationsDto>>(`${this.baseUrl}/api/user-notifications`, { params })
            .pipe(map((res) => this.normalizeNotifications(res.data)));
    }

    private approveDayOffRequestGroup(groupId: number): Observable<void> {
        return this.http
            .patch<ApiResponse<unknown>>(`${this.baseUrl}/api/day-off-request-groups/${groupId}/approve`, {})
            .pipe(map(() => void 0));
    }

    private rejectDayOffRequestGroup(groupId: number): Observable<void> {
        return this.http
            .patch<ApiResponse<unknown>>(`${this.baseUrl}/api/day-off-request-groups/${groupId}/reject`, {})
            .pipe(map(() => void 0));
    }

    private patchBookedHourApproval(
        bookedHourId: number,
        body: { isApproved?: boolean; newBookedHours?: number | null },
    ): Observable<void> {
        return this.http
            .patch<ApiResponse<unknown>>(
                `${this.baseUrl}/api/booked-hours/${bookedHourId}/approval`,
                body,
            )
            .pipe(map(() => void 0));
    }

    private resolveFullYearWeekForDate(date: Date): Observable<string> {
        const year = date.getFullYear();

        return this.getAllYywwsByYear(year).pipe(
            map((weeks) => {
                const target = this.toDateOnlyString(date);
                const match = (weeks ?? []).find(
                    (week) => week.startDate <= target && week.endDate >= target,
                );

                if (!match?.fullYearWeek) {
                    throw new Error(`No YYWW found for ${target}.`);
                }

                return match.fullYearWeek;
            }),
        );
    }

    private getAllYywwsByYear(year: number): Observable<YywwDto[]> {
        const pageSize = 100;

        return this.getYywwsPage(year, 1, pageSize).pipe(
            expand((res) => {
                const currentPage = res.currentPage ?? 1;
                const totalPages = res.totalPages ?? 1;
                const nextPage = currentPage + 1;

                return nextPage <= totalPages ? this.getYywwsPage(year, nextPage, pageSize) : EMPTY;
            }),
            reduce((all, page) => [...all, ...(page.data ?? [])], [] as YywwDto[]),
            map((weeks) =>
                [...weeks].sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
            ),
        );
    }

    private getYywwsPage(
        year: number,
        pageNumber: number,
        pageSize: number,
    ): Observable<ApiPagedResponse<YywwDto>> {
        const params = new HttpParams()
            .set('searchTerm', String(year))
            .set('pageNumber', String(pageNumber))
            .set('pageSize', String(pageSize));

        return this.http.get<ApiPagedResponse<YywwDto>>(`${this.baseUrl}/api/yywws`, { params });
    }

    private normalizeNotifications(data: UserNotificationsDto | null | undefined): UserNotificationsDto {
        return {
            yywWs: data?.yywWs ?? [],
            dayOffRequestGroupsNeedToApproveBySupervisor:
                data?.dayOffRequestGroupsNeedToApproveBySupervisor ?? [],
            bookedHoursNeedToApproveBySupervisor:
                data?.bookedHoursNeedToApproveBySupervisor ?? [],
            bookedHoursNeedToApproveBySelf:
                data?.bookedHoursNeedToApproveBySelf ?? [],
        };
    }

    private toDateOnlyString(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    requestSelfBookedHourChange(bookedHourId: number, newBookedHours: number): void {
        this.resolveAction({
            kind: 'bookedHourSelfApproval',
            id: bookedHourId,
            newBookedHours,
        }).subscribe();
    }
}
